/**
 * `useAsync` — a tiny data-fetching hook used by the route pages.
 *
 * Runs an async function, tracks loading/error/data, cancels stale results on
 * unmount or when the dependency key changes, and exposes a `reload`. Errors are
 * normalized to a string message (ApiError carries a useful `.message`).
 *
 * Deliberately minimal (no cache) — the dashboard's data needs are small and the
 * realtime channel handles freshness for the ticket feed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  data: T | undefined;
  error: string | null;
  loading: boolean;
  reload: () => void;
  /** Imperatively replace data (e.g. after a realtime patch or mutation). */
  setData: (updater: T | ((prev: T | undefined) => T)) => void;
}

export function useAsync<T>(
  fn: () => Promise<T>,
  deps: readonly unknown[] = [],
): AsyncState<T> {
  const [data, setDataState] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [nonce, setNonce] = useState(0);
  const latest = useRef(0);

  // The fetch fn changes identity each render; we intentionally key the effect
  // on the caller-provided deps + reload nonce, not on `fn`.
  const run = useCallback(fn, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ticket = ++latest.current;
    setLoading(true);
    setError(null);
    run()
      .then((result) => {
        if (ticket !== latest.current) return;
        setDataState(result);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ticket !== latest.current) return;
        setError(toMessage(e));
        setLoading(false);
      });
    return () => {
      // invalidate in-flight result
      latest.current++;
    };
  }, [run, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const setData = useCallback(
    (updater: T | ((prev: T | undefined) => T)) => {
      setDataState((prev) =>
        typeof updater === 'function'
          ? (updater as (p: T | undefined) => T)(prev)
          : updater,
      );
    },
    [],
  );

  return { data, error, loading, reload, setData };
}

/** Normalize any thrown value to a display string. */
export function toMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) {
    const m = (e as { message: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return String(e);
}
